import React, { useState, useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import SlotList from '@src/components/scheduling-provider-card/SlotList.jsx';
import { useToggle } from '@src/hooks/core/core-hook-imports.js';
import ActionButton from "../core/buttons/action-button.jsx";
import { BREAKPOINTS } from "@src/js/constants.js";
import SchedulingContext from './SchedulingContext.jsx';
import SecondaryActionButton from "../core/buttons/action-button-secondary.jsx";
import { LuxonUtils } from '@js/utils/utils-imports.es6.js';

/**
 * Renders appointment time slots in a grid by day (columns), according to the
 * provided availabilityByDays array.
 */
const SlotGrid = (props) => {
  const DESKTOP_SLOTS_PER_DAY = 4;
  const MOBILE_SLOTS_PER_DAY = 8;
  const [expanded, toggleExpanded] = useToggle(false);
  const [isViewMoreDisabled, setIsViewMoreDisabled] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < BREAKPOINTS.DESKTOP);
  const { sliderRef } = useContext(SchedulingContext);
  const [allSlotsAreEmpty, setAllSlotsAreEmpty] = useState(false);
  const [nextAvailableDate, setNextAvailableDate] = useState('');
  const [nextAvailableDateString, setNextAvailableDateString] = useState('');

  /* On slider change, we need to revisit the state of the view more/zoom button */
  const handleSliderChange = () => {
    // Access the calendarSlider instance from the ref
    const calendarSlider = sliderRef.current.slider;

    if (calendarSlider) {
      // get current day(s) shown
      // transition needs to happen first
      setTimeout(() => {
        calendarSlider.setVisibleDates();

        const visibleDates = calendarSlider.getVisibleDates();
        const datesToCheck = props.availabilityByDays;
        const matchingDates = datesToCheck.filter(([date]) => visibleDates.includes(date));

        setIsViewMoreDisabled(true);
        for (const [date, slots] of matchingDates) {
          const nonEmptySlots = slots.filter(slot => slot.slot !== '');
          const isLimitExceeded = isMobile ? nonEmptySlots.length > MOBILE_SLOTS_PER_DAY : nonEmptySlots.length > DESKTOP_SLOTS_PER_DAY;

          if (isLimitExceeded) {
            setIsViewMoreDisabled(false);
            break;
          }
        };

        const allSlotsEmpty = matchingDates.every(([date, slots]) => {
          return slots.every(slot => slot.slot === '');
        });
        setAllSlotsAreEmpty(allSlotsEmpty);
        if (allSlotsEmpty) {
          // Find the next date with a non-empty slot or after visible dates
          const nextDateWithNonEmptySlot = datesToCheck.find(([date, slots]) => {
            return date > visibleDates[visibleDates.length - 1] && slots.some(slot => slot.slot !== '');
          });

          if (nextDateWithNonEmptySlot) {
            setNextAvailableDate(nextDateWithNonEmptySlot);
            setNextAvailableDateString(LuxonUtils.convertIsoDateToStringified(nextDateWithNonEmptySlot[0]));
          } else {
            // edge case where we have no next available
            setAllSlotsAreEmpty(false);
            setNextAvailableDate(null);
            setNextAvailableDateString(null);
          }
        }
      }, 400);
    }
  };

  const zoomToAvailable = () => {
    const calendarSlider = sliderRef.current.slider;

    if (calendarSlider) {
      calendarSlider.zoomToDate(nextAvailableDate[0]);
    }
    props.triggerRedrawByIncrementing();
  }

  useEffect(() => {
    // Trigger on component load
    handleSliderChange();
  }, [props.triggerRedraw]);

  useMemo(() => {
    // Trigger on component load
    handleSliderChange();

    // Add event listeners to handle slider changes
    const leftSlider = document.getElementById('left-slider');
    const rightSlider = document.getElementById('right-slider');
    const goToStart = document.querySelector('.go-to-start');

    leftSlider.addEventListener('click', handleSliderChange);
    rightSlider.addEventListener('click', handleSliderChange);
    goToStart.addEventListener('click', handleSliderChange);

    // Clean up the event listeners on component unmount
    return () => {
      leftSlider.removeEventListener('click', handleSliderChange);
      rightSlider.removeEventListener('click', handleSliderChange);
      goToStart.removeEventListener('click', handleSliderChange);
    };
  }, [sliderRef, sliderRef.current]);

  return (
    <>
      {
        <div className='scheduling-info__time-slots'>
          {props.availabilityByDays.map(availability => {
            const [date, slots] = availability;

            return (
              <SlotList
                key={date}
                slots={slots}
                expanded={expanded}
              />
            )
          })}
        </div>
      }
        <ActionButton
          className={`scheduling-info__${expanded ? 'view-less' : 'view-more'}`}
          clickHandler={toggleExpanded}
          disabled={!expanded && (isViewMoreDisabled || allSlotsAreEmpty)}
        >
          {expanded ? props.viewFewerTimesText : props.viewMoreTimesText}
        </ActionButton>
      { allSlotsAreEmpty && (
        <SecondaryActionButton
          className='zoom-to-available'
          clickHandler={zoomToAvailable}
        >
          {`${props.nextAvailableText} ${nextAvailableDateString}`}
        </SecondaryActionButton>
      )}
    </>
  );
}

SlotGrid.propTypes = {
  /**
   * the array of available slots per day
   *
   * @example
   * [
   *  [
   *    '2023-01-01',
   *    [
   *      { ...slot1 },
   *      { ...slot2 },
   *    ]
   *  ],
   *  [
   *    '2023-01-02',
   *    [
   *      { ...slot1 },
   *    ]
   *  ]
   * ]
   */
  availabilityByDays: PropTypes.array.isRequired,
  viewMoreTimesText: PropTypes.string.isRequired,
  viewFewerTimesText: PropTypes.string.isRequired,
  triggerRedraw: PropTypes.number.isRequired,
  triggerRedrawByIncrementing: PropTypes.func.isRequired,
  nextAvailableText: PropTypes.string.isRequired,
};

export default SlotGrid;
